import { Button, RadioGroup } from '@/common'
import { useAppConfig } from '@/shared/hooks/api/appConfig'
import { AssetInstanceListItem } from './AssetInstanceListItem'
import { ProductionRouteAsset } from '@/features/production-routes/api/ProductionRouteModel'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { SingleAssetSelectDialog } from './SingleAssetSelectDialog'
import { useDialog } from '@/shared/hooks/useDialog'
import { AssetDefinition } from '@/shared/hooks/api/appConfig'
import { SingleAssetIconSelectDialog } from './SingleAssetIconSelectDialog'
import { AddAssetWithIdPayload } from '../useAssetInstances'

export default function AssetCategoryEditor({
  selectedAssets,
  addAssetWithId,
  removeAssetWithId,
  updateAssetWithId,
  id,
  category,
  assetDefinitions,
  title,
  selectionType,
  dialogTitle,
  technologyTypeQuestion,
  removeMultipleAssets,
  booleanQuestion,
  isElectrolyticCellSystem
}: {
  selectedAssets: ProductionRouteAsset[]
  title: string
  dialogTitle?: string
  selectionType?: 'icon'
  addAssetWithId: (payload: AddAssetWithIdPayload) => void
  removeAssetWithId: (assetId: number) => void
  updateAssetWithId: (assetId: number, asset: ProductionRouteAsset) => void
  removeMultipleAssets: (ids: number[]) => void
  assetDefinitions?: (AssetDefinition & {
    icon?: React.ReactNode
  })[]
  id: string
  category?: string
  booleanQuestion?: {
    name: string
    value: boolean
    question: string
    update?: (value: boolean) => void
  }
  technologyTypeQuestion?: string
  isElectrolyticCellSystem?: boolean
}) {
  const { t } = useTranslation()
  const { open, openDialog, closeDialog } = useDialog()
  const { assetsById } = useAppConfig().config

  const onAdd = useCallback(
    (payload: {
      assetDefinitionId: string | null
      customName: string | null
      isCustom: boolean
    }) => {
      addAssetWithId({
        assetDefinitionId: payload.assetDefinitionId,
        emissionPoint: id,
        isCustom: payload.isCustom,
        customName: payload.customName,
        category
      })
      closeDialog()
    },
    [addAssetWithId, id, closeDialog, category]
  )

  const onPepUsed = useCallback(
    (value: boolean) => {
      if (booleanQuestion?.update) {
        booleanQuestion?.update(value)
      }

      if (!value && removeMultipleAssets) {
        removeMultipleAssets(selectedAssets.map(asset => asset.id))
      }
    },
    [removeMultipleAssets, booleanQuestion, selectedAssets]
  )

  const isAddButtonEnabled = isElectrolyticCellSystem ? selectedAssets.length < 1 : true

  // Filter out assets that are not in the asset definitions of this specific asset category or are custom assets
  const selectedAssetsThatAreInTheAssetDefinitions = useMemo(() => {
    return selectedAssets.filter(
      asset =>
        assetDefinitions?.some(assetDefinition => assetDefinition.id === asset.assetDefinitionId) ||
        (category ? asset.category === category : asset.isCustom)
    )
  }, [selectedAssets, assetDefinitions, category])

  const assetSelectDialog = useMemo(() => {
    if (assetDefinitions) {
      return selectionType === 'icon' ? (
        <SingleAssetIconSelectDialog
          isOpen={open}
          close={closeDialog}
          title={dialogTitle}
          assetDefinitions={assetDefinitions}
          onAdd={onAdd}
        />
      ) : (
        <SingleAssetSelectDialog
          isOpen={open}
          close={closeDialog}
          assetDefinitions={assetDefinitions}
          onAdd={onAdd}
        />
      )
    }
    return <SingleAssetSelectDialog isOpen={open} close={closeDialog} onAdd={onAdd} />
  }, [assetDefinitions, open, closeDialog, onAdd, selectionType, dialogTitle])

  return (
    <>
      <div>
        <header>
          <h4 className="font-semibold">{title}</h4>
        </header>
        <div className="space-y-2">
          {booleanQuestion && (
            <>
              <p className="text-text-secondary text-sm">{booleanQuestion.question}</p>
              <RadioGroup<boolean>
                value={booleanQuestion.value || false}
                name={booleanQuestion.name}
                options={[
                  { label: t('common:yes'), value: true },
                  { label: t('common:no'), value: false }
                ]}
                onChange={onPepUsed}
              />
            </>
          )}

          {(!booleanQuestion || booleanQuestion.value) && (
            <div className="space-y-2">
              <p className="text-text-secondary">{technologyTypeQuestion}</p>
              <ul className="space-y-2">
                {selectedAssetsThatAreInTheAssetDefinitions.map(asset => (
                  <AssetInstanceListItem
                    isElectrolyticCellSystem={isElectrolyticCellSystem}
                    asset={asset}
                    key={asset.id}
                    assetDefinition={
                      asset.assetDefinitionId ? assetsById[asset.assetDefinitionId] : undefined
                    }
                    onRemove={() => removeAssetWithId(asset.id)}
                    onUpdate={newAsset => updateAssetWithId(asset.id, newAsset)}
                  />
                ))}
              </ul>

              <Button sizeType="sm" onClick={openDialog} disabled={!isAddButtonEnabled}>
                {t('common:actions.add')}
              </Button>
            </div>
          )}
        </div>
      </div>
      {assetSelectDialog}
    </>
  )
}
